/** Centralized localStorage cache — all reads/writes go through here for logging/debug */

import { CACHE_TTL_DAYS } from './constants';

const TAG = '[Cache]';

/** Save a value under key. Value is JSON-serialized automatically. Removes key if value is null/undefined. */
export function saveToCache(key, value) {
  try {
    if (value == null) {
      localStorage.removeItem(key);
    } else {
      const raw = JSON.stringify(value);
      localStorage.setItem(key, raw);
    }
  } catch (err) {
  }
}

/** Load a value by key. Returns parsed JSON or null if missing/invalid. */
export function loadFromCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw);
    return value;
  } catch (err) {
    return null;
  }
}

/** Remove a key from cache */
export function removeFromCache(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
  }
}

/**
 * Filter cache object to remove stale entries older than CACHE_TTL_DAYS (from constants.js)
 * Expects entries to have a last_accessed timestamp
 * @param {object} cache - Cache object with userId keys and { data, last_accessed } values
 * @returns {object} - Filtered cache object
 */
export function filterStaleCacheEntries(cache) {
  if (!cache || typeof cache !== 'object') return cache;

  const now = Date.now();
  const ttlMs = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;
  const filtered = {};

  for (const [key, value] of Object.entries(cache)) {
    if (value && value.last_accessed && (now - value.last_accessed) < ttlMs) {
      filtered[key] = value;
    }
  }

  return filtered;
}

/**
 * Save an entry to cache with stale filtering
 * Loads existing cache, filters stale entries, adds/updates the given entry, saves, and returns current version
 * @param {string} key - Cache key
 * @param {string} entryKey - Key for the specific entry (e.g., userId)
 * @param {any} value - Value to cache
 * @returns {object} - Updated cache object
 */
export function saveToCacheWithFilter(key, entryKey, value) {
  const cache = filterStaleCacheEntries(loadFromCache(key) || {});
  cache[entryKey] = { data: value, last_accessed: Date.now() };
  saveToCache(key, cache);
  return cache;
}

/**
 * Update the last_accessed timestamp for a cache entry
 * @param {string} key - Cache key
 * @param {string} entryKey - Key for the specific entry (e.g., userId)
 */
export function updateCacheTimestamp(key, entryKey) {
  const cache = loadFromCache(key) || {};
  if (cache[entryKey]) {
    cache[entryKey].last_accessed = Date.now();
    saveToCache(key, cache);
  }
}

/**
 * Save to chats cache with stale filtering
 * Alias for saveToCacheWithFilter for chats-specific usage
 * @param {string} entryKey - Key for the specific entry (e.g., chatId)
 * @param {any} value - Value to cache
 * @returns {object} - Updated cache object
 */
export function saveToChatsCache(entryKey, value) {
  return saveToCacheWithFilter(CACHE_KEYS.CHATS, entryKey, value);
}

/**
 * Save to profiles cache with stale filtering
 * Alias for saveToCacheWithFilter for profiles-specific usage
 * @param {string} entryKey - Key for the specific entry (e.g., userId)
 * @param {any} value - Value to cache
 * @returns {object} - Updated cache object
 */
export function saveToProfilesCache(entryKey, value) {
  return saveToCacheWithFilter(CACHE_KEYS.OTHER_PROFILES, entryKey, value);
}

/** Cache key constants used across the app */
export const CACHE_KEYS = {
  PROFILE: (userId) => `cache_profile_${userId}`,
  AUTH_USER_ID: 'cache_auth_user_id',
  OVULATION_TESTS: (userId) => `cache_ovulation_tests_${userId}`,
  RATINGS: (userId) => `cache_ratings_${userId}`,
  MATCH_SETTINGS: (userId) => `cache_match_settings_${userId}`,
  OTHER_PROFILES: 'cache_other_profiles',
  CHATS: 'cache_chats',
  TESTS: 'cache_tests',
};
